import * as React from "react";
import { Button } from "@ozpanel/ui";

// PlacementPicker — pick EXACTLY which nodes an app may run on, and how.
//
// It replaces the coarse "all / interlock-everything / pin-one" dropdown that could not express a
// SUBSET: a tenant (or admin) could not say "interlocked over node10, node11 AND node100" — the
// dropdown treated every interlocked set as the same value, so there was no way to add or remove a
// specific candidate. This is a node CHECKLIST plus a mode, and it maps to the SAME {ha_mode, nodes}
// the placement endpoint takes, so both consoles share one control (the single-source rule).
//
// The backend shapes (internal/modules/appserver/placement.go):
//   interlocked → ha_mode "singleton", 2+ candidates: exactly one runs, the rest fenced, moves on
//                 failure; a single candidate is a pin.
//   replicated  → ha_mode "replicated": a copy on every listed node; an EMPTY list means every node
//                 (and follows nodes added later).

export interface PlacementValue {
  ha_mode: string; // "singleton" | "replicated"
  nodes: string[]; // candidate node names; [] with replicated = every node
}

export interface PlacementPickerProps {
  /** Every node the app MAY run on — GET /apps/placement/nodes. */
  nodes: string[];
  /** The node the request lands on, marked "(this node)". */
  thisNode?: string;
  /** The app's current placement, so the picker opens on its real state. */
  value: PlacementValue;
  /** Called with the new {ha_mode, nodes} when the operator applies a change. */
  onApply: (next: PlacementValue) => void;
  busy?: boolean;
  applyLabel?: string;
}

type Mode = "interlocked" | "replicated";

function fromValue(v: PlacementValue, all: string[]): { mode: Mode; checked: string[] } {
  const ns = (v.nodes ?? []).filter((n) => all.includes(n));
  // singleton = one instance across the candidates (interlocked; a single candidate reads as a pin).
  if ((v.ha_mode || "singleton") === "singleton") return { mode: "interlocked", checked: ns.length ? ns : all };
  // replicated: a copy on each; an empty stored list means every node.
  return { mode: "replicated", checked: ns.length ? ns : all };
}

function toPayload(mode: Mode, checked: string[], all: string[]): PlacementValue {
  const nodes = all.filter((n) => checked.includes(n)); // stable order, only real nodes
  if (mode === "interlocked") return { ha_mode: "singleton", nodes };
  // replicated: if every node is selected, send [] so it means "all nodes" and follows new ones.
  if (nodes.length === all.length) return { ha_mode: "replicated", nodes: [] };
  return { ha_mode: "replicated", nodes };
}

const same = (a: PlacementValue, b: PlacementValue) =>
  (a.ha_mode || "singleton") === (b.ha_mode || "singleton") &&
  (a.nodes ?? []).join(",") === (b.nodes ?? []).join(",");

export function PlacementPicker({ nodes, thisNode, value, onApply, busy, applyLabel = "Apply placement" }: PlacementPickerProps) {
  const valKey = `${value.ha_mode}|${(value.nodes ?? []).join(",")}`;
  const nodesKey = nodes.join(",");
  // Depend on the STRINGIFIED placement, not the object ref, so a parent re-render does not wipe the
  // operator's in-progress selection; it re-syncs only when the stored placement actually changes.
  const init = React.useMemo(() => fromValue(value, nodes), [valKey, nodesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = React.useState<Mode>(init.mode);
  const [checked, setChecked] = React.useState<string[]>(init.checked);
  React.useEffect(() => {
    setMode(init.mode);
    setChecked(init.checked);
  }, [init.mode, init.checked.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (n: string) => setChecked((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));
  const next = toPayload(mode, checked, nodes);
  const cur = toPayload(init.mode, init.checked, nodes);
  const changed = !same(next, cur);
  const tooFew = checked.length === 0;

  const modes: [Mode, string][] = [
    ["interlocked", "One instance — moves if a node fails"],
    ["replicated", "A copy on each selected node"],
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {modes.map(([m, label]) => (
          <button
            key={m}
            type="button"
            disabled={!!busy}
            onClick={() => setMode(m)}
            className={`rounded-lg border px-2.5 py-1 text-[12px] ${mode === m ? "border-accent text-fg" : "border-line2 text-muted"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="divide-y divide-line rounded-lg border border-line2 bg-bg">
        {nodes.length === 0 && <div className="px-3 py-2 text-[12.5px] text-muted">No candidate nodes.</div>}
        {nodes.map((n) => (
          <label key={n} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px]">
            <input type="checkbox" checked={checked.includes(n)} disabled={!!busy} onChange={() => toggle(n)} />
            <span className="font-mono text-fg">{n}</span>
            {n === thisNode && <span className="text-[11px] text-muted">(this node)</span>}
          </label>
        ))}
      </div>
      <p className="text-[11.5px] leading-relaxed text-muted">
        {mode === "interlocked"
          ? `Exactly one of the ${checked.length} selected node(s) runs it; if that node fails it starts on another. The route and DNS follow it.`
          : next.nodes.length === 0
            ? "A copy runs on every node — active/active, stays up if a node goes down."
            : `A copy runs on each of the ${checked.length} selected node(s) — active/active.`}
      </p>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!!busy || !changed || tooFew} onClick={() => onApply(next)}>
          {applyLabel}
        </Button>
        {tooFew ? (
          <span className="text-[11.5px] text-crit">Select at least one node.</span>
        ) : changed ? (
          <span className="text-[11.5px] text-muted">unsaved change</span>
        ) : null}
      </div>
    </div>
  );
}
